import React, { useEffect, useState, useCallback, useMemo } from "react";
import Constant from "../../../constant/constant";
import axios from "axios";
import "./ExamResult.css";

const MAX_SCORE = 25;
const BATCHES = ["1", "2", "3", "4", "5"];

const scoreClass = (score) => {
  const pct = (Number(score) / MAX_SCORE) * 100;
  if (pct >= 60) return "ea-badge ea-badge-good";
  if (pct >= 40) return "ea-badge ea-badge-mid";
  return "ea-badge ea-badge-low";
};

const ExamResultPage = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    regNo: "",
    batch: "",
    minScore: "",
    maxScore: "",
  });

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const { regNo, batch, minScore, maxScore } = filters;
      const res = await axios.get(
        `${Constant.API_BASE_URL}sentinix/api/exam/online-exams/result`,
        { params: { regNo, batch, minScore, maxScore } }
      );
      setResults(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching results:", err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const handleChange = (e) =>
    setFilters({ ...filters, [e.target.name]: e.target.value });

  const clearFilters = () =>
    setFilters({ regNo: "", batch: "", minScore: "", maxScore: "" });

  const stats = useMemo(() => {
    if (results.length === 0) return { total: 0, avg: "-", high: "-", low: "-" };
    const scores = results.map((r) => Number(r.score) || 0);
    return {
      total: results.length,
      avg: (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1),
      high: Math.max(...scores),
      low: Math.min(...scores),
    };
  }, [results]);

  return (
    <div className="ea-page">
      <div className="ea-header">
        <div>
          <h2>📊 Exam Results</h2>
          <p>View and filter student scores by batch, register number or score range.</p>
        </div>
      </div>

      <div className="ea-stats">
        <div className="ea-stat">
          <div className="ea-stat-icon">👥</div>
          <div>
            <div className="ea-stat-value">{stats.total}</div>
            <div className="ea-stat-label">Students</div>
          </div>
        </div>
        <div className="ea-stat">
          <div className="ea-stat-icon">📈</div>
          <div>
            <div className="ea-stat-value">{stats.avg}</div>
            <div className="ea-stat-label">Average</div>
          </div>
        </div>
        <div className="ea-stat">
          <div className="ea-stat-icon">🏆</div>
          <div>
            <div className="ea-stat-value">{stats.high}</div>
            <div className="ea-stat-label">Highest</div>
          </div>
        </div>
        <div className="ea-stat">
          <div className="ea-stat-icon">📉</div>
          <div>
            <div className="ea-stat-value">{stats.low}</div>
            <div className="ea-stat-label">Lowest</div>
          </div>
        </div>
      </div>

      <div className="ea-card ea-filters">
        <div className="ea-field">
          <label>Search</label>
          <input
            className="ea-input"
            type="text"
            name="regNo"
            value={filters.regNo}
            onChange={handleChange}
            placeholder="Register number"
          />
        </div>
        <div className="ea-field">
          <label>Batch</label>
          <select className="ea-select" name="batch" value={filters.batch} onChange={handleChange}>
            <option value="">All Batches</option>
            {BATCHES.map((b) => (
              <option key={b} value={b}>Batch {b}</option>
            ))}
          </select>
        </div>
        <div className="ea-field">
          <label>Score from</label>
          <input
            className="ea-input"
            type="number"
            name="minScore"
            value={filters.minScore}
            onChange={handleChange}
            placeholder="Min"
          />
        </div>
        <div className="ea-field">
          <label>Score to</label>
          <input
            className="ea-input"
            type="number"
            name="maxScore"
            value={filters.maxScore}
            onChange={handleChange}
            placeholder="Max"
          />
        </div>
        <button className="ea-btn ea-btn-ghost" onClick={clearFilters}>
          Clear
        </button>
      </div>

      <div className="ea-card">
        <div className="ea-table-wrap">
          <table className="ea-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Reg No</th>
                <th>Name</th>
                <th>Email</th>
                <th>Department</th>
                <th>Batch</th>
                <th>Year</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              {results.length > 0 ? (
                results.map((item, index) => (
                  <tr key={item.id ?? index}>
                    <td data-label="#" className="ea-muted">{index + 1}</td>
                    <td data-label="Reg No">{item.regNo}</td>
                    <td data-label="Name" className="ea-name">{item.name}</td>
                    <td data-label="Email" className="ea-muted">{item.email}</td>
                    <td data-label="Department">{item.department}</td>
                    <td data-label="Batch">{item.batch}</td>
                    <td data-label="Year">{item.year}</td>
                    <td data-label="Score">
                      <span className={scoreClass(item.score)}>
                        {item.score}/{MAX_SCORE}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="ea-empty ea-empty-cell">
                    <div className="ea-empty-icon">{loading ? "⏳" : "🗂️"}</div>
                    {loading ? "Loading results..." : "No results found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {results.length > 0 && (
          <div className="ea-count">Showing {results.length} result(s)</div>
        )}
      </div>
    </div>
  );
};

export default ExamResultPage;
